import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { EditableDataGrid, GridColumn, ProductSearchResult } from '@/app/pages/sales-orders/components/EditableDataGrid';
import { JobCardItemCreate, ItemCompletionStatus } from '@/app/types/jobCard';
import { Item } from '@/app/types/item';
import { getItemPricingDetails } from '@/app/lib/invoiceCalculations';
import { getItemByIdAsync } from '@/lib/indexeddb/database';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, SkipForward, Circle } from 'lucide-react';
import { TechnicianSelector } from './TechnicianSelector';

interface JobCardItemRow extends JobCardItemCreate {
  id?: number;
  item?: Item;
  completion_status?: ItemCompletionStatus;
}

interface JobCardItemsGridProps {
  items: JobCardItemRow[];
  onChange: (items: JobCardItemRow[]) => void;
  disabled?: boolean;
  showCompletionStatus?: boolean;
  technicians?: Array<{ id: number; name: string }>;
}

const STATUS_OPTIONS: { value: ItemCompletionStatus; label: string; icon: React.ReactNode; className: string }[] = [
  { value: 'pending', label: 'Pending', icon: <Circle className="h-3 w-3" />, className: 'text-muted-foreground' },
  { value: 'in_progress', label: 'In Progress', icon: <Loader2 className="h-3 w-3" />, className: 'text-blue-600' },
  { value: 'completed', label: 'Completed', icon: <CheckCircle2 className="h-3 w-3" />, className: 'text-green-600' },
  { value: 'skipped', label: 'Skipped', icon: <SkipForward className="h-3 w-3" />, className: 'text-orange-500' },
];

function getStatusLabel(status: ItemCompletionStatus): string {
  return STATUS_OPTIONS.find(s => s.value === status)?.label || status;
}

// Convert a JobCardItemRow to the grid's Record<string, unknown> format
function toGridRow(row: JobCardItemRow, technicians?: Array<{ id: number; name: string }>): Record<string, unknown> {
  const quantity = Number(row.quantity) || 0;
  const rate = Number(row.rate) || 0;
  const discountPercent = Number(row.discount_percent) || 0;
  const grossAmount = quantity * rate;
  const discountAmount = (grossAmount * discountPercent) / 100;
  const taxableAmount = grossAmount - discountAmount;

  const techName = row.technician_id
    ? technicians?.find(t => t.id === row.technician_id)?.name || `#${row.technician_id}`
    : '\u2014';

  return {
    item_id: row.item_id,
    item_name: row.item?.name || '',
    item_code: row.item?.code || '',
    quantity: row.quantity,
    unit_id: row.unit_id,
    rate: row.rate,
    discount_percent: row.discount_percent || 0,
    discount_amount: discountAmount,
    taxable_amount: taxableAmount,
    tax_rate_id: row.tax_rate_id || null,
    tax_amount: 0,
    tax_rate_percent: 0,
    is_tax_inclusive: false,
    amount: taxableAmount,
    estimated_duration_minutes: row.estimated_duration_minutes ?? '',
    notes: row.notes || '',
    technician_name: techName,
    completion_status: getStatusLabel(row.completion_status || 'pending'),
    rate_decimal_places: 0,
    // Preserve original data for conversion back
    _item: row.item,
    _id: row.id,
    _unit_id: row.unit_id,
    _technician_id: row.technician_id,
    _completion_status: row.completion_status || 'pending',
  };
}

// Convert grid data back to JobCardItemRow
function fromGridRow(gridRow: Record<string, unknown>): JobCardItemRow {
  return {
    id: gridRow._id as number | undefined,
    item_id: Number(gridRow.item_id) || 0,
    item: gridRow._item as Item | undefined,
    quantity: parseFloat(String(gridRow.quantity || 0)),
    unit_id: Number(gridRow._unit_id || gridRow.unit_id) || 0,
    rate: parseFloat(String(gridRow.rate || 0)),
    discount_percent: parseFloat(String(gridRow.discount_percent || 0)),
    discount_amount: parseFloat(String(gridRow.discount_amount || 0)),
    tax_rate_id: gridRow.tax_rate_id ? Number(gridRow.tax_rate_id) : null,
    technician_id: gridRow._technician_id ? Number(gridRow._technician_id) : null,
    estimated_duration_minutes: gridRow.estimated_duration_minutes ? Number(gridRow.estimated_duration_minutes) : null,
    notes: gridRow.notes ? String(gridRow.notes) : null,
    completion_status: (gridRow._completion_status as ItemCompletionStatus) || 'pending',
  };
}

export const JobCardItemsGrid: React.FC<JobCardItemsGridProps> = ({
  items,
  onChange,
  disabled = false,
  showCompletionStatus = false,
  technicians = [],
}) => {
  const [lineItems, setLineItemsInternal] = useState<Record<string, unknown>[]>(
    () => items.map(i => toGridRow(i, technicians))
  );
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const isMounted = useRef(false);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  // Flag to prevent circular updates between lineItems ↔ parent form items
  const isInternalChangeRef = useRef(false);

  // State for the floating status popover
  const [statusPopover, setStatusPopover] = useState<{
    rowIndex: number;
    rect: DOMRect;
  } | null>(null);

  // State for the floating technician popover
  const [technicianPopover, setTechnicianPopover] = useState<{
    rowIndex: number;
    rect: DOMRect;
  } | null>(null);

  // Sync from external prop changes (e.g., form reset, initial edit load)
  const prevItemsRef = useRef(items);
  useEffect(() => {
    // Skip if this change was triggered by our own propagation effect
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      prevItemsRef.current = items;
      return;
    }
    if (prevItemsRef.current !== items) {
      prevItemsRef.current = items;
      setLineItemsInternal(items.map(i => toGridRow(i, technicians)));
    }
  }, [items, technicians]);

  // Propagate internal grid changes to parent (skip initial mount)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    isInternalChangeRef.current = true;
    const converted = lineItems.map(fromGridRow);
    onChangeRef.current(converted);
  }, [lineItems]);

  // Column definitions
  const columns: GridColumn[] = useMemo(() => {
    const cols: GridColumn[] = [
      { field: 'item_id', header: 'Product', type: 'product', width: '200px', sortable: true },
      { field: 'quantity', header: 'Qty', type: 'number', width: '80px', sortable: true },
      { field: 'rate', header: 'Rate', type: 'price', width: '100px', sortable: true },
      { field: 'discount_percent', header: 'Disc %', type: 'number', width: '80px', sortable: false },
      { field: 'taxable_amount', header: 'Taxable', type: 'price', editable: false, width: '100px', sortable: false },
      { field: 'tax_amount', header: 'Tax', type: 'price', editable: false, width: '80px', sortable: false },
      { field: 'amount', header: 'Amount', type: 'price', editable: false, width: '100px', sortable: true },
      { field: 'estimated_duration_minutes', header: 'Est. Min', type: 'number', width: '80px', sortable: false },
      { field: 'notes', header: 'Notes', type: 'text', width: '150px', sortable: false },
      { field: 'technician_name', header: 'Tech', type: 'text', editable: false, width: '100px', sortable: false },
    ];
    if (showCompletionStatus) {
      cols.push({ field: 'completion_status', header: 'Status', type: 'text', editable: false, width: '100px', sortable: false });
    }
    cols.push({ field: 'actions', header: '', type: 'actions', width: '50px', sortable: false });
    return cols;
  }, [showCompletionStatus]);

  // Find the DOM column index for the status field (accounting for the NO. column the grid adds)
  const statusDomColIndex = useMemo(() => {
    if (!showCompletionStatus) return -1;
    const idx = columns.findIndex(c => c.field === 'completion_status');
    return idx >= 0 ? idx + 1 : -1; // +1 for the NO. column
  }, [columns, showCompletionStatus]);

  // Find the DOM column index for the technician field
  const technicianDomColIndex = useMemo(() => {
    const idx = columns.findIndex(c => c.field === 'technician_name');
    return idx >= 0 ? idx + 1 : -1; // +1 for the NO. column
  }, [columns]);

  // Click handler on grid wrapper — detect clicks on status and technician cells
  const handleGridClick = useCallback((e: React.MouseEvent) => {
    if (disabled) return;

    const target = e.target as HTMLElement;
    const cell = target.closest('td');
    if (!cell) return;

    const row = cell.closest('tr');
    if (!row) return;

    const tbody = row.closest('tbody');
    if (!tbody) return;

    const rowIndex = Array.from(tbody.children).indexOf(row);
    const cellIndex = Array.from(row.children).indexOf(cell);

    if (cellIndex === statusDomColIndex && statusDomColIndex >= 0 && rowIndex >= 0) {
      const rect = cell.getBoundingClientRect();
      setStatusPopover({ rowIndex, rect });
    }

    if (cellIndex === technicianDomColIndex && technicianDomColIndex >= 0 && rowIndex >= 0) {
      const rect = cell.getBoundingClientRect();
      setTechnicianPopover({ rowIndex, rect });
    }
  }, [disabled, statusDomColIndex, technicianDomColIndex]);

  // Close popover on outside click
  useEffect(() => {
    if (!statusPopover) return;
    let attached = false;
    const handleClickOutside = (e: MouseEvent) => {
      const popoverEl = document.getElementById('jc-status-popover');
      if (popoverEl && !popoverEl.contains(e.target as Node)) {
        setStatusPopover(null);
      }
    };
    // Delay to avoid closing immediately from the same click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      attached = true;
    }, 0);
    return () => {
      clearTimeout(timer);
      if (attached) {
        document.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, [statusPopover]);

  // Close technician popover on outside click
  useEffect(() => {
    if (!technicianPopover) return;
    let attached = false;
    const handleClickOutside = (e: MouseEvent) => {
      const popoverEl = document.getElementById('jc-technician-popover');
      if (popoverEl && !popoverEl.contains(e.target as Node)) {
        setTechnicianPopover(null);
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      attached = true;
    }, 0);
    return () => {
      clearTimeout(timer);
      if (attached) {
        document.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, [technicianPopover]);

  // Handle status change for a specific row
  const handleStatusChange = useCallback((rowIndex: number, newStatus: ItemCompletionStatus) => {
    setLineItemsInternal(currentItems => {
      const newItems = [...currentItems];
      if (!newItems[rowIndex]) return currentItems;
      newItems[rowIndex] = {
        ...newItems[rowIndex],
        _completion_status: newStatus,
        completion_status: getStatusLabel(newStatus),
      };
      return newItems;
    });
    setStatusPopover(null);
  }, []);

  // Handle technician change for a specific row
  const handleTechnicianChange = useCallback((rowIndex: number, technicianId: number | undefined, technician?: { id: number; name: string }) => {
    setLineItemsInternal(currentItems => {
      const newItems = [...currentItems];
      if (!newItems[rowIndex]) return currentItems;
      newItems[rowIndex] = {
        ...newItems[rowIndex],
        _technician_id: technicianId || null,
        technician_name: technician?.name || '\u2014',
      };
      return newItems;
    });
    setTechnicianPopover(null);
  }, []);

  // Style status cells to look clickable after render
  useEffect(() => {
    if (!showCompletionStatus || statusDomColIndex < 0 || !gridWrapperRef.current) return;
    const tbody = gridWrapperRef.current.querySelector('tbody');
    if (!tbody) return;

    Array.from(tbody.children).forEach((row) => {
      const cells = row.children;
      const statusCell = cells[statusDomColIndex] as HTMLElement | undefined;
      if (statusCell) {
        statusCell.style.cursor = disabled ? 'default' : 'pointer';
        statusCell.style.userSelect = 'none';
        // Add a subtle styling to indicate clickability
        const status = lineItems[Array.from(tbody.children).indexOf(row)]?._completion_status as ItemCompletionStatus;
        if (status === 'completed') {
          statusCell.style.color = '#16a34a';
          statusCell.style.fontWeight = '500';
        } else if (status === 'in_progress') {
          statusCell.style.color = '#2563eb';
          statusCell.style.fontWeight = '500';
        } else if (status === 'skipped') {
          statusCell.style.color = '#f97316';
          statusCell.style.fontWeight = '500';
        } else {
          statusCell.style.color = '';
          statusCell.style.fontWeight = '';
        }
      }
    });
  }, [showCompletionStatus, statusDomColIndex, lineItems, disabled]);

  // Style technician cells to look clickable after render
  useEffect(() => {
    if (technicianDomColIndex < 0 || !gridWrapperRef.current) return;
    const tbody = gridWrapperRef.current.querySelector('tbody');
    if (!tbody) return;

    Array.from(tbody.children).forEach((row) => {
      const cells = row.children;
      const techCell = cells[technicianDomColIndex] as HTMLElement | undefined;
      if (techCell) {
        techCell.style.cursor = disabled ? 'default' : 'pointer';
        techCell.style.userSelect = 'none';
      }
    });
  }, [technicianDomColIndex, lineItems, disabled]);

  // Product search function (ItemSearchModal does its own IndexedDB search internally)
  const searchProducts = useCallback(async (_searchTerm: string): Promise<ProductSearchResult[]> => {
    return [];
  }, []);

  // Handle product selection — fetch pricing details with tax
  const handleProductSelect = useCallback(async (product: ProductSearchResult, rowIndex: number): Promise<void> => {
    try {
      const enrichedItem = await getItemByIdAsync(product.id);
      const unitId = enrichedItem?.unit_id || 0;

      const pricingResult = await getItemPricingDetails(product.id, 1);

      setLineItemsInternal(currentItems => {
        const newItems = [...currentItems];
        if (!newItems[rowIndex]) return currentItems;

        const rate = pricingResult ? pricingResult.rate : 0;
        const discountPercent = pricingResult ? pricingResult.discount_percent : 0;
        const discountAmount = pricingResult ? pricingResult.discount_amount : 0;
        const taxableAmount = pricingResult ? pricingResult.taxable_amount : 0;
        const taxAmount = pricingResult ? pricingResult.tax_amount : 0;
        const amount = pricingResult ? pricingResult.amount : 0;

        newItems[rowIndex] = {
          ...newItems[rowIndex],
          item_id: product.id,
          item_name: product.name,
          item_code: product.code || '',
          quantity: 1,
          unit_id: unitId,
          rate,
          discount_percent: discountPercent,
          discount_amount: discountAmount,
          taxable_amount: taxableAmount,
          tax_rate_id: pricingResult ? pricingResult.tax_rate_id : null,
          tax_amount: taxAmount,
          amount,
          applied_sale_rule_id: pricingResult ? pricingResult.applied_sale_rule_id : null,
          is_tax_inclusive: pricingResult ? pricingResult.is_tax_inclusive : false,
          tax_rate_percent: pricingResult?.tax_breakdown
            ? pricingResult.tax_breakdown.reduce((sum, component) => sum + component.rate, 0)
            : 0,
          rate_decimal_places: product.rate_decimal_places || 0,
          _item: {
            id: product.id,
            name: product.name,
            code: product.code || undefined,
            item_type: enrichedItem?.item_type || 'service',
            hsn_code: enrichedItem?.hsn_code,
            base_unit_id: unitId,
            tax_rate_id: product.tax_rate_id || null,
            sale_price: String(enrichedItem?.prices?.sale_price || '0'),
          } as Item,
          _unit_id: unitId,
        };
        return newItems;
      });
    } catch (error) {
      toast.error(`Error calculating price: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, []);

  // Handle quantity change for rows with applied sale rules
  const handleQuantityChangeForRow = useCallback(async (rowIndex: number, newQuantity: number): Promise<void> => {
    const item = lineItems[rowIndex];
    if (!item) return;

    const itemId = Number(item.item_id);
    if (!itemId) return;

    try {
      const pricingResult = await getItemPricingDetails(itemId, newQuantity);
      if (pricingResult) {
        setLineItemsInternal(currentItems => {
          const newItems = [...currentItems];
          newItems[rowIndex] = {
            ...newItems[rowIndex],
            rate: pricingResult.rate,
            discount_percent: pricingResult.discount_percent,
            discount_amount: pricingResult.discount_amount,
            taxable_amount: pricingResult.taxable_amount,
            tax_rate_id: pricingResult.tax_rate_id,
            tax_amount: pricingResult.tax_amount,
            amount: pricingResult.amount,
          };
          return newItems;
        });
      }
    } catch (error) {
      console.error('Error recalculating price for quantity change:', error);
    }
  }, [lineItems]);

  // Calculate total from grid data
  const total = lineItems.reduce((sum, row) => sum + (parseFloat(String(row.amount || 0))), 0);

  return (
    <div className="space-y-4">
      <div className="hidden sm:flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Press <kbd className="px-1 py-0.5 border rounded text-xs">Tab</kbd> to navigate |{' '}
          <kbd className="px-1 py-0.5 border rounded text-xs">Enter</kbd> to select
        </div>
      </div>
      {/* Wrap grid to intercept clicks on status cells */}
      <div ref={gridWrapperRef} onClick={handleGridClick}>
        <EditableDataGrid
          columns={columns}
          data={lineItems}
          setData={setLineItemsInternal}
          productSearchFn={searchProducts}
          disabled={disabled}
          onProductSelect={handleProductSelect}
          onQuantityChangeForRow={handleQuantityChangeForRow}
        />
      </div>
      {/* Floating status popover rendered via portal */}
      {statusPopover && createPortal(
        <div
          id="jc-status-popover"
          className="fixed z-50 bg-popover border rounded-md shadow-md p-1 min-w-[140px]"
          style={{
            top: statusPopover.rect.bottom + 4,
            left: statusPopover.rect.left,
          }}
        >
          {STATUS_OPTIONS.map((option) => {
            const currentStatus = lineItems[statusPopover.rowIndex]?._completion_status as ItemCompletionStatus;
            return (
              <Button
                key={option.value}
                variant={currentStatus === option.value ? 'secondary' : 'ghost'}
                size="sm"
                className={`w-full justify-start gap-2 text-sm ${option.className}`}
                onClick={() => handleStatusChange(statusPopover.rowIndex, option.value)}
              >
                {option.icon}
                {option.label}
              </Button>
            );
          })}
        </div>,
        document.body
      )}
      {/* Floating technician popover rendered via portal */}
      {technicianPopover && !disabled && createPortal(
        <div
          id="jc-technician-popover"
          style={{
            position: 'fixed',
            top: technicianPopover.rect.bottom + 4,
            left: technicianPopover.rect.left,
            zIndex: 9999,
          }}
          className="bg-popover border rounded-lg shadow-lg p-2 w-[250px]"
        >
          <TechnicianSelector
            value={lineItems[technicianPopover.rowIndex]?._technician_id as number | undefined}
            onChange={(techId, technician) => {
              handleTechnicianChange(
                technicianPopover.rowIndex,
                techId,
                technician ? { id: technician.id, name: technician.name } : undefined
              );
            }}
            placeholder="Select technician..."
          />
        </div>,
        document.body
      )}
      <div className="flex justify-end">
        <div className="text-lg font-semibold">
          Total:{' '}
          {total.toLocaleString('en-IN', {
            style: 'currency',
            currency: 'INR',
          })}
        </div>
      </div>
    </div>
  );
};
